import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import TaskOverview from './TaskOverview'
import TaskDetail from './TaskDetail'
import ReadingView from './ReadingView'
import {
  courseIdOf,
  filterHomeworks,
  homeworkIdOf,
  sortHomeworks,
} from './homeworkUtils'
import './HomeworkWorkbench.css'

/**
 * 训练任务工作台：层1 总览 → 层2 单任务 → 层3 评价/成长
 */
export default function HomeworkWorkbench({
  open,
  onClose,
  homeworks = [],
  userCourses = [],
  archivesList = [],
  archivesLoading = false,
  archivesError = '',
  focusTaskId = null,
  focusMode = null,
  focusCourseId = null,
  isFromMenu = false,
  accentColor = '#6f9fff',
  onStartTraining,
  onSubmitArchive,
  submitting = false,
  getStudentId,
  buildIdentityHeaders,
  onRefresh,
  fetchArchives,
}) {
  const { t } = useTranslation()
  const [layer, setLayer] = useState('overview') // overview | detail | reading
  const [selectedId, setSelectedId] = useState(null)
  const [courseFilter, setCourseFilter] = useState('all')
  const [statusFilter, setStatusFilter] = useState('all')
  const [keyword, setKeyword] = useState('')
  const [sortBy, setSortBy] = useState('path')
  const [drafts, setDrafts] = useState({})
  const [justSubmittedId, setJustSubmittedId] = useState(null)
  const [reading, setReading] = useState(null) // { mode, data, title }
  const focusAppliedRef = useRef('')

  const filteredSorted = useMemo(() => {
    const courseId = courseFilter === 'all'
      ? 'all'
      : (userCourses.find((c) => String(c.id) === String(courseFilter) || String(c.course_id) === String(courseFilter))?.course_id
        ?? courseFilter)
    return sortHomeworks(filterHomeworks(homeworks, {
      courseId,
      statusFilter,
      keyword,
    }), sortBy)
  }, [homeworks, userCourses, courseFilter, statusFilter, keyword, sortBy])

  const selected = useMemo(
    () => homeworks.find((hw) => homeworkIdOf(hw) === Number(selectedId)) || null,
    [homeworks, selectedId],
  )

  // 打开 / 深链
  useEffect(() => {
    if (!open) {
      focusAppliedRef.current = ''
      setLayer('overview')
      setSelectedId(null)
      setReading(null)
      setJustSubmittedId(null)
      setKeyword('')
      setStatusFilter('all')
      setSortBy('path')
      return
    }

    const targetId = Number(focusTaskId)
    const targetCourseId = Number(focusCourseId)
    if (!targetId) {
      if (targetCourseId) setCourseFilter(String(targetCourseId))
      if (focusMode === 'growth' && targetCourseId) {
        const applyKey = `growth:${targetCourseId}`
        if (focusAppliedRef.current === applyKey) return
        focusAppliedRef.current = applyKey
        getStudentId?.().then((studentId) => {
          if (!studentId) return
          setReading({
            mode: 'growth',
            title: '课程成长轨迹',
            data: { courseId: targetCourseId, studentId, courseName: '' },
          })
          setLayer('reading')
        })
        return
      }
      setLayer((prev) => (prev === 'reading' ? prev : 'overview'))
      return
    }
    if (!homeworks?.length) {
      return
    }
    const applyKey = `${targetId}:${focusMode || 'homework'}`
    if (focusAppliedRef.current === applyKey) return
    const target = homeworks.find((hw) => homeworkIdOf(hw) === targetId)
    if (!target) return

    focusAppliedRef.current = applyKey
    setSelectedId(targetId)
    if (target.CourseID != null || target.courseId != null) {
      const cid = courseIdOf(target)
      const course = userCourses.find((c) => Number(c.course_id) === Number(cid))
      if (course) setCourseFilter(String(course.course_id))
    }

    if (focusMode === 'evaluation') {
      const archiveId = target.archiveId || target.ArchiveId
      if (archiveId) {
        setReading({
          mode: 'evaluation',
          title: target.title || target.Title || '作业',
          data: { archiveId, standard: null, homeworkTitle: target.title || target.Title },
        })
        setLayer('reading')
        return
      }
    }
    setLayer('detail')
    // getStudentId 由父级每次渲染创建，刻意不加入依赖以避免重复打开成长报告。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, focusTaskId, focusMode, focusCourseId, homeworks, userCourses])

  const requestArchives = (homework) => {
    if (!homework || homework.trainingType === 'chatlogs' || homework.trainingType === 'lab_report') return
    return fetchArchives?.()
  }

  const openDetail = (hw) => {
    setSelectedId(homeworkIdOf(hw))
    setLayer('detail')
    setJustSubmittedId(null)
  }

  const openEvaluation = (hw) => {
    const archiveId = hw.archiveId || hw.ArchiveId
    if (!archiveId) {
      window.alert('该作业尚未提交存档，无法查看评价')
      return
    }
    setSelectedId(homeworkIdOf(hw))
    setReading({
      mode: 'evaluation',
      title: hw.title || hw.Title || '作业',
      data: { archiveId, standard: null },
    })
    setLayer('reading')
  }

  const crumb = (() => {
    if (layer === 'overview') {
      return <span className="hwb-crumb-current">{t('hw_title')}</span>
    }
    if (layer === 'detail') {
      return (
        <>
          <button type="button" onClick={() => setLayer('overview')}>全部作业</button>
          <span className="hwb-crumb-sep">/</span>
          <span className="hwb-crumb-current">{selected?.title || selected?.Title || '作业'}</span>
        </>
      )
    }
    return (
      <>
        <button type="button" onClick={() => setLayer('overview')}>全部作业</button>
        <span className="hwb-crumb-sep">/</span>
        {selected ? (
          <>
            <button type="button" onClick={() => setLayer('detail')}>
              {selected.title || selected.Title || '作业'}
            </button>
            <span className="hwb-crumb-sep">/</span>
          </>
        ) : null}
        <span className="hwb-crumb-current">
          {reading?.mode === 'growth' ? '成长轨迹' : '评价报告'}
        </span>
      </>
    )
  })()

  const draftKey = selectedId ? String(selectedId) : ''
  const draft = drafts[draftKey] || {}

  return (
    <div className="hwb" style={{ '--hwb-accent': accentColor || '#6f9fff' }}>
      <div className="hwb-topbar">
        <div className="hwb-crumb">{crumb}</div>
        <button type="button" className="hwb-close" onClick={onClose} aria-label="关闭">×</button>
      </div>

      <div className="hwb-body">
        {layer === 'reading' && reading ? (
          <ReadingView
            mode={reading.mode}
            data={reading.data}
            onClose={() => setLayer(selected ? 'detail' : 'overview')}
          />
        ) : (
          <div className="hwb-scroll">
            {layer === 'overview' ? (
              <TaskOverview
                homeworks={filteredSorted}
                userCourses={userCourses}
                courseFilter={courseFilter}
                statusFilter={statusFilter}
                keyword={keyword}
                sortBy={sortBy}
                onCourseFilterChange={setCourseFilter}
                onStatusFilterChange={setStatusFilter}
                onKeywordChange={setKeyword}
                onSortByChange={setSortBy}
                onSelectHomework={openDetail}
              />
            ) : null}

            {layer === 'detail' && selected ? (
              <TaskDetail
                homework={selected}
                orderedList={filteredSorted}
                archivesList={archivesList}
                archivesLoading={archivesLoading}
                archivesError={archivesError}
                draft={draft}
                onDraftChange={(next) => setDrafts((prev) => ({ ...prev, [draftKey]: next }))}
                submitting={submitting}
                justSubmittedId={justSubmittedId}
                onClearJustSubmitted={() => setJustSubmittedId(null)}
                onSelectHomework={openDetail}
                onOpenEvaluation={openEvaluation}
                onStartTraining={onStartTraining}
                isFromMenu={isFromMenu}
                getStudentId={getStudentId}
                buildIdentityHeaders={buildIdentityHeaders}
                onRefresh={onRefresh}
                onRequestArchives={requestArchives}
                onSubmitArchive={async (homeworkId, archiveId, message) => {
                  const ok = await onSubmitArchive?.(homeworkId, archiveId, message)
                  if (ok === true) {
                    setJustSubmittedId(homeworkId)
                    setDrafts((prev) => ({
                      ...prev,
                      [String(homeworkId)]: { ...(prev[String(homeworkId)] || {}), archiveId, message: '' },
                    }))
                  }
                  return ok === true
                }}
              />
            ) : null}

            {layer === 'detail' && !selected ? (
              <div className="hwb-empty">
                未找到该作业
                <div style={{ marginTop: 12 }}>
                  <button type="button" className="hwb-btn hwb-btn-secondary" onClick={() => setLayer('overview')}>
                    返回全部作业
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  )
}
